import { buildEvidenceFilter, filters, transformers } from './transformers';
import { isCategorical, isIndividualEvidence } from '../../typeguards/index';
import { cloneDeep } from '../object-utils';

const _transformAndFilter = (
  codes: CMxAPI.MedicalCode[],
  artifactFilter: CMxArtifacts.ArtifactFilters
) => {
  const { transformers = [], predicates = [] } = artifactFilter;
  if (transformers.length) {
    transformers.forEach(transformer => {
      codes = codes.map(transformer) as CMxAPI.MedicalCode[];
    });
  }
  if (predicates.length) {
    predicates.forEach(predicate => {
      codes = codes.filter(predicate) as CMxAPI.MedicalCode[];
    });
  }

  return codes;
};

const _transformArtifact = (
  artifact: CMxAPI.CaseArtifacts,
  artifactFilter: CMxArtifacts.ArtifactFilters
) => {
  let { procedures } = artifact;
  procedures.codes = _transformAndFilter(
    procedures.codes,
    artifactFilter
  ) as CMxAPI.ProcedureCode[];

  const {
    diagnoses: { codes: diagnosticCodes = [] },
    modifiers: { codes: modifierCodes = [] }
  } = artifact;

  if (diagnosticCodes.length) {
    artifact.diagnoses.codes = _transformAndFilter(
      diagnosticCodes,
      artifactFilter
    ) as CMxAPI.DiagnosisCode[];
  }

  if (modifierCodes.length) {
    artifact.modifiers.codes = _transformAndFilter(
      modifierCodes,
      artifactFilter
    ) as CMxAPI.ModifierCode[];
  }
  return artifact;
};

const transformArtifacts = (
  srcArtifacts: CMxAPI.CaseArtifacts[],
  caseRun: CMxAPI.CaseRun,
  includer: CMxArtifacts.EvidencePredicate
) => {
  const artifacts = cloneDeep(srcArtifacts);

  const filterIncluder = buildEvidenceFilter(includer);

  // first pass to get rid of unknown evidence.
  // we _should_ rework further code to know nothing
  // about unknonwn evidence.
  const onlyKnown = artifacts.map(artifact => {
    const mutator = {
      transformers: [],
      predicates: [filterIncluder, filters.onlyKnownEvidence]
    };
    return _transformArtifact(artifact, mutator);
  });

  const processed = onlyKnown.map(artifact => {
    const mutator = {
      transformers: [
        transformers.convertToCategorical,
        transformers.normalizeType,
        transformers.weedOutNonSentenceNgrams,
        transformers.splitFeatures,
        transformers.uuidEvidence,
        transformers.deduper,
        transformers.uniqueEvidenceSpans,
        transformers.normalizeTextContent,
        transformers.hashNgrams,
        transformers.uuidCodes
      ],
      predicates: [filters.hasEvidenceFilter] //
    };

    return _transformArtifact(artifact, mutator);
  });

  //console.log(JSON.stringify(processed));

  // fix categorical
  // to make the ngrams *look* like categorical features, we
  // need access to the CaseRun data. i have some ideas on how
  // to do this within the transformers frameworker, but it requires
  // a bigger rework than time permits.  so for now, there's
  // kinda hacky solution here.
  const shimCategorical = (piece: CMxAPI.UnionEvidence) => {
    if (isIndividualEvidence(piece)) {
      const { feature } = piece;
      if (isCategorical(feature) && feature.category === 'something') {
        // @ts-ignore
        feature.category = caseRun[feature.field] || 'unknown value';
      }
    }
  };

  processed.forEach(artifact => {
    artifact.procedures.codes.forEach(code => {
      code.evidence?.forEach(shimCategorical);
      code.diagnoses?.codes?.forEach(code => {
        code.evidence?.forEach(shimCategorical);
      });
      code.modifiers?.codes?.forEach(code => {
        code.evidence?.forEach(shimCategorical);
      });
    });

    artifact.diagnoses?.codes.forEach(code => {
      code.evidence?.forEach(shimCategorical);
    });
    artifact.modifiers?.codes.forEach(code => {
      code.evidence?.forEach(shimCategorical);
    });
  });

  return processed;
};

const defaultEvidenceFilter: CMxArtifacts.EvidencePredicate = (
  evidenceItem: CMxAPI.UnionEvidence
) => {
  return true;
};

export const preprocessor = (
  originalRun: CMxAPI.CaseRun,
  evidenceFilter: CMxArtifacts.EvidencePredicate = defaultEvidenceFilter
) => {
  const caseRun = cloneDeep(originalRun);
  // ipc cases no longer have artifacts.
  const { artifacts = [] } = caseRun;
  const filtered = transformArtifacts(artifacts, caseRun, evidenceFilter);
  caseRun.artifacts = filtered;
  return caseRun;
};
